import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { PawPrint, Heart, Sparkles, Star } from "lucide-react";
import { LOGO_URL, api } from "@/lib/api";

export default function Hero() {
  const [happyCount, setHappyCount] = useState(0);
  const [avgRating, setAvgRating] = useState(5);

  useEffect(() => {
    api
      .get("/reviews")
      .then((r) => {
        const happy = (r.data || []).filter((x) => x.rating >= 4);
        setHappyCount(happy.length);
        if (happy.length > 0) {
          const sum = happy.reduce((s, x) => s + x.rating, 0);
          setAvgRating((sum / happy.length).toFixed(1));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden pt-10 pb-24 sm:pt-16 sm:pb-32"
    >
      {/* Painted backdrop */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://customer-assets.emergentagent.com/job_happy-tails-aurora/artifacts/q1pt0e9z_Snapchat-1837480879.jpg"
          alt=""
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-[#F4E8C8]/80" />
      </div>

      {/* Floating motifs */}
      <PawPrint className="absolute top-20 left-[6%] w-8 h-8 text-[#3F2A56]/15 -rotate-12 float-slow hidden sm:block" />
      <PawPrint className="absolute top-40 right-[10%] w-10 h-10 text-[#4A6741]/20 rotate-12 float-slow hidden sm:block" style={{ animationDelay: "1.2s" }} />
      <Heart className="absolute bottom-32 left-[12%] w-7 h-7 text-[#b85b7e]/25 float-slow hidden sm:block" style={{ animationDelay: "2.4s" }} />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
        <div>
          <span
            data-testid="hero-eyebrow"
            className="sticker-tape font-script text-xl"
          >
            <Sparkles className="inline w-4 h-4 mr-1 -mt-0.5" />
            Byron-Bergen&rsquo;s gentlest pet care
          </span>

          <h1
            data-testid="hero-title"
            className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl text-[#3F2A56] leading-[1.02] mt-6"
          >
            Loving care
            <br />
            while you&rsquo;re{" "}
            <span className="hand-underline italic text-[#b85b7e]">away.</span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-7 text-lg sm:text-xl text-[#5C4A73] max-w-xl leading-relaxed"
          >
            Walks, cuddles, and peace of mind — delivered by Aurora, who treats
            every wagging tail and curled-up paw like her very own.{" "}
            <em className="text-[#3F2A56] not-italic font-semibold">
              Your pet&rsquo;s comfort is always the top priority.
            </em>
          </p>

          <div className="mt-9 flex flex-wrap gap-3 items-center">
            <Link
              to="/book"
              data-testid="hero-book-btn"
              className="rounded-full bg-[#3F2A56] hover:bg-[#523870] text-[#F4E8C8] font-semibold px-7 py-3.5 text-[15px] transition-all hover:-translate-y-0.5 shadow-md"
            >
              Book a service
            </Link>
            <a
              href="#services"
              data-testid="hero-services-btn"
              className="rounded-full bg-transparent border-2 border-[#3F2A56] text-[#3F2A56] font-semibold px-7 py-3.5 text-[15px] transition-colors hover:bg-[#3F2A56]/5"
            >
              See pricing
            </a>
            <Link
              to="/meet-greet"
              data-testid="hero-meet-greet-btn"
              className="font-script text-[#4A6741] text-xl ml-2 hover:text-[#3F2A56] transition-colors underline-offset-4 hover:underline"
            >
              ↳ free meet &amp; greet!
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
            <Link
              to="/reviews"
              data-testid="hero-trust-badge"
              className="flex items-center gap-3 group"
            >
              <div className="flex -space-x-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-9 h-9 rounded-full border-2 border-[#F4E8C8] grid place-items-center shadow-sm"
                    style={{
                      background: ["#3F2A56", "#b85b7e", "#4A6741"][i],
                      transform: `rotate(${(i - 1) * 6}deg)`,
                    }}
                  >
                    <PawPrint className="w-4 h-4 text-[#F4E8C8]" />
                  </div>
                ))}
              </div>
              <div>
                {happyCount > 0 ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]" />
                      ))}
                      <span className="font-display font-bold text-[#3F2A56] ml-1">
                        {avgRating}
                      </span>
                    </div>
                    <div className="text-sm text-[#5C4A73] group-hover:text-[#3F2A56] transition-colors">
                      Trusted by{" "}
                      <strong
                        data-testid="happy-count"
                        className="text-[#3F2A56] font-display text-base"
                      >
                        {happyCount}
                      </strong>{" "}
                      happy pet parent{happyCount === 1 ? "" : "s"}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-[#5C4A73] group-hover:text-[#3F2A56] transition-colors leading-tight">
                    <div className="font-display font-semibold text-[#3F2A56]">
                      Byron-Bergen, NY
                    </div>
                    <div>Locally owned · pet-first care</div>
                  </div>
                )}
              </div>
            </Link>
            <span className="text-[#3F2A56]/25 hidden sm:inline">·</span>
            <Stat number="Free" label="meet & greet" />
            <span className="text-[#3F2A56]/25 hidden sm:inline">·</span>
            <Stat number="7-day" label="availability" />
          </div>
        </div>

        <div className="relative">
          <div className="relative aspect-square max-w-md mx-auto">
            <div className="absolute -inset-4 rounded-full bg-[#FFFBF0] border-2 border-[#3F2A56]/15 rotate-1" />
            <div className="absolute inset-0 rounded-full overflow-hidden border-4 border-[#3F2A56]/25 shadow-2xl bg-[#FFFBF0]">
              <img
                src={LOGO_URL}
                alt="Happy Tails by Aurora — illustrated logo with dogs and cats"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -left-2 sm:-left-6 bg-[#FFFBF0] rounded-2xl px-4 py-3 border-2 border-[#3F2A56]/20 shadow-lg rotate-[-4deg] max-w-[200px]">
              <div className="font-script text-2xl text-[#3F2A56] leading-tight">
                Walks, cuddles
              </div>
              <div className="font-script text-2xl text-[#b85b7e] leading-tight">
                &amp; peace of mind.
              </div>
            </div>
            <div className="absolute -top-2 -right-2 bg-[#3F2A56] text-[#F4E8C8] rounded-full w-20 h-20 grid place-items-center rotate-[8deg] shadow-lg">
              <div className="text-center leading-tight">
                <div className="font-display text-xl font-bold">100%</div>
                <div className="text-[10px] uppercase tracking-wider">pet first</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-display font-bold text-2xl text-[#3F2A56] leading-none">
        {number}
      </div>
      <div className="text-xs uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}
